<script setup lang="ts">
/**
 * A multi-line field bound with `v-model`. `focus()` is exposed because the
 * composer moves focus into it when the host page opens the panel; nothing else
 * reaches into the element.
 *
 * The attributes it forwards are DECLARED, not left to fall through, so a typo at
 * a call site is a vue-tsc error rather than an attribute nobody reads.
 */
import { useTemplateRef } from 'vue';

defineProps<{
  readonly id?: string | undefined;
  readonly rows?: number | undefined;
  readonly required?: boolean | undefined;
  readonly maxlength?: number | undefined;
  readonly placeholder?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly invalid?: boolean | undefined;
  readonly label?: string | undefined;
  readonly describedBy?: string | undefined;
}>();

const emit = defineEmits<{ keydown: [event: KeyboardEvent] }>();

const model = defineModel<string>({ required: true });
const field = useTemplateRef<HTMLTextAreaElement>('field');

defineExpose({
  focus: (): void => {
    field.value?.focus();
  },
});
</script>

<template>
  <textarea
    :id="id"
    ref="field"
    v-model="model"
    :rows="rows"
    :required="required"
    :maxlength="maxlength"
    :placeholder="placeholder"
    :disabled="disabled"
    :aria-invalid="invalid === true ? true : undefined"
    :aria-label="label"
    :aria-describedby="describedBy"
    @keydown="emit('keydown', $event)"
  />
</template>
